import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FaChevronDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useLayout } from '../PageLayout/LayoutContext';
import {
  DASHBOARD_LINK,
  SECTION_NAV,
  getNavContext,
  isNavItemActive
} from './navConfig';
import './NavBar.css';

function NavItem({ to, label, icon: Icon, isActive, onClick, isSub = false }) {
  return (
    <li>
      <NavLink
        to={to}
        className={`app-sidebar-link${isActive ? ' is-active' : ''}${isSub ? ' is-sub' : ''}`}
        title={label}
        onClick={onClick}
      >
        <span className="app-sidebar-link-icon">
          <Icon aria-hidden="true" />
        </span>
        <span className="app-sidebar-link-label">{label}</span>
      </NavLink>
    </li>
  );
}

function SectionBlock({ section, isExpanded, pathname, onNavigate, onSectionToggle }) {
  const Icon = section.icon;

  if (section.status === 'coming-soon') {
    return (
      <div className="app-sidebar-section-block">
        <button
          type="button"
          className="app-sidebar-link app-sidebar-section-header is-disabled"
          disabled
          title={`${section.label} — coming soon`}
        >
          <span className="app-sidebar-link-icon">
            <Icon aria-hidden="true" />
          </span>
          <span className="app-sidebar-link-label">{section.label}</span>
          <span className="app-sidebar-soon">Soon</span>
        </button>
      </div>
    );
  }

  return (
    <div className={`app-sidebar-section-block${isExpanded ? ' is-expanded' : ''}`}>
      <button
        type="button"
        className="app-sidebar-link app-sidebar-section-header"
        onClick={() => onSectionToggle(section.id)}
        title={section.label}
        aria-expanded={isExpanded}
      >
        <span className="app-sidebar-link-icon">
          <Icon aria-hidden="true" />
        </span>
        <span className="app-sidebar-link-label">{section.label}</span>
        <span className="app-sidebar-section-chevron" aria-hidden="true">
          {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
        </span>
      </button>

      {isExpanded && section.items.length > 0 && (
        <ul className="app-sidebar-subitems">
          {section.items.map(({ to, label, icon, match, exact }) => (
            <NavItem
              key={to}
              to={to}
              label={label}
              icon={icon}
              isActive={isNavItemActive(pathname, match, exact)}
              onClick={onNavigate}
              isSub
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function Navbar() {
  const { sidebarCollapsed, toggleSidebar, closeSidebar, isMobile } = useLayout();
  const location = useLocation();
  const navContext = getNavContext(location.pathname);
  const [expandedSections, setExpandedSections] = useState(() => (
    navContext !== 'dashboard' ? new Set([navContext]) : new Set()
  ));

  useEffect(() => {
    if (navContext === 'dashboard') {
      return;
    }
    setExpandedSections((prev) => {
      if (prev.has(navContext)) {
        return prev;
      }
      return new Set([...prev, navContext]);
    });
  }, [navContext]);

  const handleNavClick = () => {
    if (isMobile) {
      closeSidebar();
    }
  };

  const handleSectionToggle = (sectionId) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  return (
    <aside className={`app-sidebar${sidebarCollapsed ? ' is-collapsed' : ''}`} aria-hidden={sidebarCollapsed}>
      {!sidebarCollapsed && !isMobile && (
        <button
          type="button"
          className="app-sidebar-edge-toggle"
          onClick={toggleSidebar}
          aria-label="Hide sidebar"
          title="Hide menu"
        >
          <FaChevronLeft aria-hidden="true" />
        </button>
      )}

      <div className="app-sidebar-inner">
        <NavLink to="/home" className="app-sidebar-brand" onClick={handleNavClick}>
          <img src="/assets/logo.png" alt="" className="app-sidebar-logo" />
          <div className="app-sidebar-brand-text">
            <span className="app-sidebar-brand-name">Shivam Buildcare</span>
            <span className="app-sidebar-brand-tag">Business workspace</span>
          </div>
        </NavLink>

        <nav className="app-sidebar-nav" aria-label="Main navigation">
          <ul className="app-sidebar-home-link">
            <NavItem
              to={DASHBOARD_LINK.to}
              label={DASHBOARD_LINK.label}
              icon={DASHBOARD_LINK.icon}
              isActive={location.pathname === '/home'}
              onClick={handleNavClick}
            />
          </ul>

          <div className="app-sidebar-sections">
            {SECTION_NAV.map((section) => (
              <SectionBlock
                key={section.id}
                section={section}
                isExpanded={expandedSections.has(section.id)}
                pathname={location.pathname}
                onNavigate={handleNavClick}
                onSectionToggle={handleSectionToggle}
              />
            ))}
          </div>
        </nav>
      </div>
    </aside>
  );
}

export default Navbar;
